import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useEditMode } from '../../hooks/useEditMode';
import ReplaceImageButton from '../ReplaceImageButton';
import { StyledImgDivContainerSlot } from '../../dto/StyleCompRepository';
import defaultImg from '../../../asset/project/default_1.png'
import { ImageMetadata } from '../../dto/EntityRepository';
import { isBase64, getFallbackDeviceType, convertToJsonMetadata, isDeviceAvailable, generateOptimizedUrl } from '../../util/findAdjustImg';
import { debounce } from '../../util/debounce';

interface OptimizedMoleculeImgDivContainerProps {
  backgroundImg: string;
  handleChange?: (originUrl: string, width: number, height: number) => void;
  StyledImgDivContainer: StyledImgDivContainerSlot;
  children?: React.ReactNode;
  [key: string]: any; // Allow additional props to be passed through
}

const OptimizedMoleculeImgDivContainer: React.FC<OptimizedMoleculeImgDivContainerProps> = ({
  backgroundImg,
  handleChange,
  StyledImgDivContainer,
  children,
  ...restProps
}) => {
  const { isEditMode } = useEditMode();
  const [metadata, setMetadata] = useState<ImageMetadata | null>(null);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const [browserType, setBrowserType] = useState<string | null>(null);
  const [adjustedUrl, setAdjustedUrl] = useState<string | null>(null);
  const currentDeviceTypeRef = useRef<string | null>(null);
  const previousAdjustedUrlRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isHttpUrl = Boolean(backgroundImg && backgroundImg.startsWith('http'));
  const isBase64Img = isBase64(backgroundImg);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const jsonUrl = backgroundImg.replace(/\/[^/]+\.(?:jpeg|jpg|png|gif|webp)$/, `/meta.json`);
        const response = await fetch(jsonUrl, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to fetch metadata');
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          const imageMetadata = convertToJsonMetadata(data);

          setMetadata(imageMetadata);
          setMetadataFetched(true);

          if (containerRef.current) {
            const containerWidth =
              containerRef.current.offsetWidth ||
              containerRef.current.getBoundingClientRect().width;
            const deviceType = getFallbackDeviceType(imageMetadata, containerWidth);
            if (currentDeviceTypeRef.current !== deviceType) {
              setBrowserType(deviceType);
              currentDeviceTypeRef.current = deviceType;
            }
          }
        } else {
          throw new Error('Response is not JSON');
        }
      } catch {
        setMetadata(null);
        setMetadataFetched(true);
      }
    };

    if (backgroundImg && !isBase64Img && isHttpUrl) {
      setMetadataFetched(false);
      setBrowserType(null);
      currentDeviceTypeRef.current = null;
      fetchMetadata();
    } else {
      setMetadata(null);
      setMetadataFetched(true);
    }
  }, [backgroundImg, isBase64Img, isHttpUrl]);

  useEffect(() => {
    if (!containerRef.current || !metadata || isBase64Img || !isHttpUrl) return;

    const debouncedCallback = debounce((entry: ResizeObserverEntry) => {
      const width = entry.contentRect.width;
      const newDeviceType = getFallbackDeviceType(metadata, width);
      if (currentDeviceTypeRef.current !== newDeviceType) {
        setBrowserType(newDeviceType);
        currentDeviceTypeRef.current = newDeviceType;
      }
    }, 200);

    const ro = new ResizeObserver(([entry]) => {
      debouncedCallback(entry);
    });

    ro.observe(containerRef.current);

    const initialWidth =
      containerRef.current.offsetWidth ||
      containerRef.current.getBoundingClientRect().width;
    const initialDeviceType = getFallbackDeviceType(metadata, initialWidth);
    if (currentDeviceTypeRef.current !== initialDeviceType) {
      setBrowserType(initialDeviceType);
      currentDeviceTypeRef.current = initialDeviceType;
    }

    return () => {
      ro.disconnect();
      debouncedCallback.cancel();
    };
  }, [metadata, backgroundImg, isBase64Img, isHttpUrl]);

  useEffect(() => {
    let newUrl: string | null = null;

    if (isBase64Img || !isHttpUrl) {
      newUrl = backgroundImg;
    } else if (!metadataFetched) {
      newUrl = null;
    } else if (!metadata) {
      newUrl = backgroundImg;
    } else if (
      backgroundImg &&
      metadata &&
      browserType &&
      isDeviceAvailable(metadata, browserType)
    ) {
      newUrl = generateOptimizedUrl(backgroundImg, browserType, metadata);
    } else {
      newUrl = backgroundImg;
    }

    if (previousAdjustedUrlRef.current !== newUrl) {
      setAdjustedUrl(newUrl);
      previousAdjustedUrlRef.current = newUrl;
    }
  }, [browserType, backgroundImg, metadata, metadataFetched, isBase64Img, isHttpUrl]);

  const finalBackgroundImg = useMemo(() => {
    return backgroundImg === '' ? defaultImg : (adjustedUrl || defaultImg);
  }, [backgroundImg, adjustedUrl]);

  return (
    <StyledImgDivContainer ref={containerRef} $backgroundImg={finalBackgroundImg} {...restProps}>
      {handleChange && isEditMode && (
        <ReplaceImageButton setImageUrl={handleChange} />
      )}
      {children}
    </StyledImgDivContainer>
  );
}


export default OptimizedMoleculeImgDivContainer;
